/* globals analytics, ga */

import trackingService from '../state/services/tracking';

// Data events are bound to a unique URL handler
export const DATA_EVENTS = {
  ACCOUNT_LOG_IN: 'account-log-in',
  ACCOUNT_REGISTRATION: 'account-registration',
  BOOKING_COMPLETE: 'booking-complete',
  BOOKING_START: 'booking-start',
  COMPETITION_SHARE: 'competition-share',
  EVENT_REVIEW: 'event-review',
  EVENT_SAVE: 'event-save',
  EVENT_SHARE: 'event-share',
  EVENT_TICKETS: 'event-tickets',
  EVENT_UNSAVE: 'event-unsave',
  EXTERNALBOOKING_SAVE: 'externalbooking-save',
  EXTERNALBOOKING_UNSAVE: 'externalbooking-unsave',
  EVENT_VISIT: 'event-visit',
  TICKET_CLICK_OUT: 'ticket-click-out',
};

/**
 * Public methods
 *
 * These provide components with a single method to perform one or many tracking functions.
 * For example, 'login' or 'registration' methods trigger predetermined sequences of Segment calls.
 * These methods are called instead of interacting with the tracking service directly.
 *
 * TODO: These could be reworked into a decorator or HOC to save container code calling on mount.
 */

/**
 * Track page visit.
 */
export const trackPageVisit = (name, delay) => {
  trackingService.trackPage(name, delay);
};

/**
 * Track an account login.
 */
export const trackAccountLogin = (profile, returnUrl) => {
  trackingService.trackDataEvent(DATA_EVENTS.ACCOUNT_LOG_IN, {
    identify: true,
    anonymousId: analytics.user().anonymousId(),
    userId: profile.primaryIdentityId,
    returnUrl,
  });
};

/**
 * Track an account registration (and login).
 * "Account created" is tracked on both the client-side and server-side.
 * The destinations which don't support the server-side call are targetted
 * in the integrations object of the client-side call.
 */
export const trackAccountRegistrationLogin = (profile, returnUrl) => {
  ga('send', {
    hitType: 'event',
    eventCategory: 'All',
    eventAction: 'Account Created',
  });
  trackingService.trackDataEvent(DATA_EVENTS.ACCOUNT_REGISTRATION, {
    identify: true,
    anonymousId: analytics.user().anonymousId(),
    userId: profile.primaryIdentityId,
    returnUrl,
  });
  setTimeout(() => {
    trackingService.trackEvent('Account Created (client-side)', {});
    trackingService.trackDataEvent(DATA_EVENTS.ACCOUNT_LOG_IN, {
      userId: profile.primaryIdentityId,
      returnUrl,
    });
  }, 500);
};

/**
 * Track booking completed.
 * "Booking Completed" is tracked on both the client-side and server-side.
 * The destinations which don't support the server-side call are targetted
 * in the integrations object of the client-side call.
 */
export const trackBookingComplete = (
  ticketId, bookingId, eventId, friendsCount, revenue, currency, source,
) => {
  ga('send', {
    hitType: 'event',
    eventCategory: 'All',
    eventAction: 'Booking Completed',
  });
  if (revenue && currency) {
    trackingService.trackEvent('Booking Completed (client-side)', {
      content_type: 'product',
      content_ids: eventId.toString(),
      order_id: bookingId,
      revenue,
      currency,
    });
    ga('ecommerce:addTransaction', {
      id: bookingId,
      revenue,
      currency,
    });
    ga('ecommerce:send');
  } else {
    trackingService.trackEvent('Booking Completed (client-side)', {
      content_type: 'product',
      content_ids: eventId.toString(),
    });
  }
  trackingService.trackDataEvent(
    DATA_EVENTS.BOOKING_COMPLETE,
    { ticketId, bookingId, friendsCount, source },
  );
};

/**
 * Track booking started.
 */
export const trackBookingStarted = (ticket, source) => {
  trackingService.trackDataEvent(DATA_EVENTS.BOOKING_START, { ticketId: ticket.id, source });
  trackingService.trackEvent('Booking Started (client-side)', { content_type: 'product', content_ids: ticket.event.id.toString() });
  trackPageVisit('Booking Form');
};

/**
 * Track competition shared.
 */
export const trackCompetitionShared = (sharedVia) => {
  trackingService.trackDataEvent(DATA_EVENTS.COMPETITION_SHARE, { sharedVia });
};

/**
 * Track event reviewed.
 */
export const trackEventReviewed = (eventId, reviewId) => {
  trackingService.trackDataEvent(DATA_EVENTS.EVENT_REVIEW, { eventId, reviewId });
};

/**
 * Track event saved.
 */
export const trackEventSaved = (event) => {
  trackingService.trackDataEvent(DATA_EVENTS.EVENT_SAVE, { eventId: event.id });
};

/**
 * Track event shared.
 */
export const trackEventShared = (event, sharedVia) => {
  trackingService.trackDataEvent(DATA_EVENTS.EVENT_SHARE, { eventId: event.id, sharedVia });
};

/**
 * Track event unsaved.
 */
export const trackEventUnsaved = (event) => {
  trackingService.trackDataEvent(DATA_EVENTS.EVENT_UNSAVE, { eventId: event.id });
};

/**
 * Track external booking saved.
 */
export const trackExternalBookingSaved = (event) => {
  trackingService.trackDataEvent(DATA_EVENTS.EXTERNALBOOKING_SAVE, { eventId: event.id });
};

/**
 * Track external booking unsaved.
 */
export const trackExternalBookingUnsaved = (event) => {
  trackingService.trackDataEvent(DATA_EVENTS.EXTERNALBOOKING_UNSAVE, { eventId: event.id });
};

/**
 * Track event visited.
 */
export const trackEventVisit = (event, source) => {
  trackingService.trackDataEvent(DATA_EVENTS.EVENT_VISIT, { eventId: event.id, source });
  trackingService.trackEvent('Event Visited (client-side)', { content_type: 'product', content_ids: event.id.toString() });
  trackPageVisit('Event');
};

/**
 * Track event tickets visited.
 */
export const trackTicketsVisit = (event, source) => {
  trackingService.trackDataEvent(DATA_EVENTS.EVENT_TICKETS, {
    eventId: event.id,
    source,
  });
  trackPageVisit('Event Tickets');
};

/**
 * Track ticket click out.
 */
export const trackTicketClick = (ticket) => {
  if (ticket.charityTickets || (ticket.bookingPartner && ticket.bookingPartner.typeDescriptor === 'Active.com')) {
    const orderId = (new Date()).getTime();
    const currency = ticket.currencyCode;
    const ticketPrice = (ticket.discountPrice || ticket.rrp) + (ticket.fee || 0);
    let revenue;
    if (ticket.bookingDetails && ticket.bookingDetails.price) {
      const bookingPrice = ticket.bookingDetails && ticket.bookingDetails.price;
      revenue = Math.round((ticketPrice - bookingPrice) * 100) / 100;
    }

    const trackEvent = event => trackingService.trackEvent(event, {
      order_id: orderId,
      revenue,
      currency,
    });

    const trackGaEvent = eventAction => ga('send', {
      hitType: 'event',
      eventCategory: 'All',
      eventAction,
    });

    const trackGaTransaction = () => {
      if (revenue && currency) {
        ga('ecommerce:addTransaction', {
          id: orderId,
          revenue,
          currency,
        });
        ga('ecommerce:send');
      }
    };

    if (ticket.charityTickets) {
      trackEvent('Charity Ticket Clickout (client-side)');
      trackGaEvent('Charity Ticket Clickout');
      trackGaTransaction();
    }
    if (ticket.bookingPartner && ticket.bookingPartner.typeDescriptor === 'Active.com') {
      trackEvent('Active Ticket Clickout (client-side)');
      trackGaEvent('Active Ticket Clickout');
      trackGaTransaction();
    }
  }

  trackingService.trackDataEvent(DATA_EVENTS.TICKET_CLICK_OUT, { ticketId: ticket.id });
};



// WEBPACK FOOTER //
// ./app/src/utils/tracking.js